import { useAuthStore, useMyUserStore, useRolesStore } from '@/stores';
import { generateUserSetupKey } from '@/utils/localStorageKeys';
import { QUERY_PARAM_PATH_AFTER_LOGIN } from '@/constants';
import {
  ROUTE_ADVERTISER_DASHBOARD,
  ROUTE_ADVERTISER_SETUP,
  ROUTE_ERROR,
  ROUTE_HOST_DASHBOARD,
  ROUTE_HOST_HOME,
  ROUTE_HOST_SETUP,
  ROUTE_LOGIN,
  ROUTE_SYSTEM_DASHBOARD,
  ROUTE_UNAUTHORIZED,
} from './routes';

export const publicGuard = (to, from, next) => {
  const authStore = useAuthStore();
  if (!authStore.isAuthenticated) {
    next();
    return;
  }
  next('/');
};

export const authHostGuard = async (to) => {
  try {
    const authStore = useAuthStore();
    const myUserStore = useMyUserStore();
    const rolesStore = useRolesStore();

    await myUserStore.getMyUser();

    if (!authStore.isAuthenticated) {
      return {
        query: {
          [QUERY_PARAM_PATH_AFTER_LOGIN]: to.fullPath,
        },
        name: ROUTE_LOGIN,
      };
    }

    await rolesStore.getRoles();

    if (myUserStore.isHostUser && myUserStore.isAdminUser) {
      // allow user to navigate to setup page if exact match.
      // this prevents infinite loop when userHasSeenSetupPage === false
      if (to.name === ROUTE_HOST_SETUP) {
        return true;
      }

      const setupKey = generateUserSetupKey(myUserStore.userId);
      const userHasSeenSetupPage = !!localStorage.getItem(setupKey);
      if (userHasSeenSetupPage) {
        return true;
      }
      return {
        name: ROUTE_HOST_SETUP,
      };
    }
    if (myUserStore.isHostUser) {
      return true;
    }
    if (myUserStore.isAdvertiserUser) {
      return { name: ROUTE_ADVERTISER_DASHBOARD };
    }
    if (myUserStore.isSystemUser) {
      return { name: ROUTE_SYSTEM_DASHBOARD };
    }

    return { name: ROUTE_UNAUTHORIZED };
  } catch (error) {
    return { name: ROUTE_ERROR };
  }
};

export const beforeEnterHostSetupGuard = (to) => {
  const authStore = useAuthStore();
  const myUserStore = useMyUserStore();

  if (!authStore.isAuthenticated) {
    return {
      query: {
        [QUERY_PARAM_PATH_AFTER_LOGIN]: to.fullPath,
      },
      name: ROUTE_LOGIN,
    };
  }

  const setupKey = generateUserSetupKey(myUserStore.userId);
  localStorage.setItem(setupKey, true);

  return true;
};

export const authAdvertiserGuard = async (to) => {
  try {
    const authStore = useAuthStore();
    const myUserStore = useMyUserStore();
    const rolesStore = useRolesStore();

    await myUserStore.getMyUser();

    if (!authStore.isAuthenticated) {
      return {
        query: {
          [QUERY_PARAM_PATH_AFTER_LOGIN]: to.fullPath,
        },
        name: ROUTE_LOGIN,
      };
    }

    await rolesStore.getRoles();

    if (myUserStore.isAdvertiserUser) {
      // allow user to navigate to setup page if exact match.
      // this prevents infinite loop when userHasSeenSetupPage === false
      if (to.name === ROUTE_ADVERTISER_SETUP) {
        return true;
      }

      const setupKey = generateUserSetupKey(myUserStore.userId);
      const userHasSeenSetupPage = !!localStorage.getItem(setupKey);
      if (userHasSeenSetupPage) {
        return true;
      }
      return {
        name: ROUTE_ADVERTISER_SETUP,
      };
    }

    if (myUserStore.isHostUser) {
      return { name: ROUTE_HOST_DASHBOARD };
    }
    if (myUserStore.isSystemUser) {
      return { name: ROUTE_SYSTEM_DASHBOARD };
    }

    return { name: ROUTE_UNAUTHORIZED };
  } catch (error) {
    return { name: ROUTE_ERROR };
  }
};

export const beforeEnterAdvertiserSetupGuard = (to) => {
  const authStore = useAuthStore();
  const myUserStore = useMyUserStore();

  if (!authStore.isAuthenticated) {
    return {
      query: {
        [QUERY_PARAM_PATH_AFTER_LOGIN]: to.fullPath,
      },
      name: ROUTE_LOGIN,
    };
  }

  const setupKey = generateUserSetupKey(myUserStore.userId);
  localStorage.setItem(setupKey, true);

  return true;
};

export const beforeEnterHostOrganizationGuard = async (to, from, next) => {
  const myUserStore = useMyUserStore();

  if (myUserStore.myOrganization && myUserStore.canAccessHostOrganizationPage) {
    next();
  } else {
    next({ name: ROUTE_HOST_HOME });
  }
};

export const authSystemGuard = async (to) => {
  try {
    const authStore = useAuthStore();
    const myUserStore = useMyUserStore();
    const rolesStore = useRolesStore();

    await myUserStore.getMyUser();

    if (!authStore.isAuthenticated) {
      return {
        query: {
          [QUERY_PARAM_PATH_AFTER_LOGIN]: to.fullPath,
        },
        name: ROUTE_LOGIN,
      };
    }

    await rolesStore.getRoles();

    if (myUserStore.isSystemUser) {
      return true;
    }

    if (myUserStore.isHostUser) {
      return { name: ROUTE_HOST_DASHBOARD };
    }
    if (myUserStore.isAdvertiserUser) {
      return { name: ROUTE_ADVERTISER_DASHBOARD };
    }

    return { name: ROUTE_UNAUTHORIZED };
  } catch (error) {
    return { name: ROUTE_ERROR };
  }
};

export const authAdminGuard = async (to) => {
  try {
    const authStore = useAuthStore();
    const myUserStore = useMyUserStore();
    const rolesStore = useRolesStore();

    await myUserStore.getMyUser();

    if (!authStore.isAuthenticated) {
      return {
        query: {
          [QUERY_PARAM_PATH_AFTER_LOGIN]: to.fullPath,
        },
        name: ROUTE_LOGIN,
      };
    }

    await rolesStore.getRoles();

    if (myUserStore.isSystemAdmin) {
      return true;
    }

    if (myUserStore.isHostUser) {
      return { name: ROUTE_HOST_DASHBOARD };
    }
    if (myUserStore.isAdvertiserUser) {
      return { name: ROUTE_ADVERTISER_DASHBOARD };
    }
    if (myUserStore.isSystemUser) {
      return { name: ROUTE_SYSTEM_DASHBOARD };
    }

    return { name: ROUTE_UNAUTHORIZED };
  } catch (error) {
    return { name: ROUTE_ERROR };
  }
};
